import { PaymentElement } from "@stripe/react-stripe-js";
import { ConfirmationToken, StripePaymentElementOptions } from "@stripe/stripe-js";
import configuration from "../../configuration";
import { PaymentMethodPreview } from "../PaymentMethodPreview";
import { usePaymentContext } from "../../contexts/payment";

interface Props {
  isEditingPaymentMethod: boolean;
  confirmationToken: ConfirmationToken | undefined;
  setIsEditingPaymentMethod: React.Dispatch<React.SetStateAction<boolean>>;
  onPaymentElementReady: () => void;
  onPaymentElementLoadError: () => void;
}

export function PurchasePayment({
  isEditingPaymentMethod,
  confirmationToken,
  setIsEditingPaymentMethod,
  onPaymentElementReady,
  onPaymentElementLoadError,
}: Props) {
  const { useInvoicePayment, setUseInvoicePayment } = usePaymentContext();

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: {
      type: "accordion",
      defaultCollapsed: false,
      radios: true,
      spacedAccordionItems: false,
    },
    business: { name: configuration.business.name },
    paymentMethodOrder: ["paypal", "klarna", "card", "sofort"],
  };

  return (
    <>
      <div className="font-bold mt-7 mb-3">Zahlung</div>

      {/* Horsia: Segmented Control — dezenter Toggle zwischen Stripe-Zahlung und "Auf Rechnung" */}
      {(
      <div
        role="tablist"
        aria-label="Zahlungsart"
        className="relative inline-flex items-center p-1 bg-neutral-100 rounded-full mb-5 text-sm"
      >
        <button
          type="button"
          role="tab"
          aria-selected={!useInvoicePayment}
          onClick={() => setUseInvoicePayment(false)}
          className={`relative z-10 px-5 py-2 rounded-full transition-colors ${
            !useInvoicePayment ? "text-neutral-900 font-medium" : "text-neutral-500 hover:text-neutral-700"
          }`}
        >
          <span
            className={`absolute inset-0 rounded-full transition-all ${
              !useInvoicePayment ? "bg-white shadow-sm" : ""
            }`}
            aria-hidden="true"
          />
          <span className="relative">Jetzt bezahlen</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={useInvoicePayment}
          onClick={() => setUseInvoicePayment(true)}
          className={`relative z-10 px-5 py-2 rounded-full transition-colors ${
            useInvoicePayment ? "text-neutral-900 font-medium" : "text-neutral-500 hover:text-neutral-700"
          }`}
        >
          <span
            className={`absolute inset-0 rounded-full transition-all ${
              useInvoicePayment ? "bg-white shadow-sm" : ""
            }`}
            aria-hidden="true"
          />
          <span className="relative">Auf Rechnung</span>
        </button>
      </div>
      )}

      {useInvoicePayment ? (
        <div className="p-4 bg-neutral-100 rounded-md text-sm">
          Du erhältst nach Abschluss deines Auftrags eine Rechnung per E-Mail oder Post. Bitte überweise
          den Rechnungsbetrag innerhalb der angegebenen Zahlungsfrist auf das angegebene Konto.
        </div>
      ) : confirmationToken && !isEditingPaymentMethod ? (
        <div>
          <span className="flex items-center gap-4 mb-3 justify-between">
            <span>Gewählte Zahlungsmethode</span>
            <button
              type="button"
              onClick={() => {
                setIsEditingPaymentMethod(true);
              }}
              className="text-sm text-neutral-900 underline"
            >
              Ändern
            </button>
          </span>
          <PaymentMethodPreview paymentMethodPreview={confirmationToken.payment_method_preview} />
        </div>
      ) : (
        <>
          <PaymentElement
            id="payment-element"
            options={paymentElementOptions}
            onReady={() => onPaymentElementReady()}
            onLoadError={() => onPaymentElementLoadError()}
          />
          {confirmationToken && (
            <button
              type="button"
              onClick={() => {
                setIsEditingPaymentMethod(false);
              }}
              className="text-sm text-neutral-900 underline mt-4"
            >
              Abbrechen
            </button>
          )}
        </>
      )}
    </>
  );
}
